import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  Users, UserPlus, Search, Pencil, Power, Link2, Copy, Check, Loader2, ChevronLeft, ChevronRight,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Sheet, SheetContent, SheetTitle } from '@/components/ui/sheet';
import {
  fetchAdminUsers, createUser, updateUser, resendInvite, type AdminUserFilters,
} from '@/hooks/useAdmin';
import type { AdminUserItem } from '@uniticket/shared';

const PAGE_SIZE = 50;
const ROLES = ['SYSTEM_ADMIN', 'UNIFIBER_OPERATOR', 'BUILD'] as const;
const AUTH_TYPES = ['LOCAL', 'ENTRA_ID', 'BOTH'] as const;

interface FormState {
  email: string;
  displayName: string;
  role: string;
  authType: string;
}

function formatDate(iso: string | null, fallback: string): string {
  if (!iso) return fallback;
  const d = new Date(iso);
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

export function UserManagement() {
  const { t } = useTranslation('common');
  const { user: me } = useAuth();
  const [users, setUsers] = useState<AdminUserItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState<'' | 'true' | 'false'>('');
  const [offset, setOffset] = useState(0);

  const [dialog, setDialog] = useState<{ mode: 'create' | 'edit'; user?: AdminUserItem } | null>(null);
  const [form, setForm] = useState<FormState>({ email: '', displayName: '', role: 'UNIFIBER_OPERATOR', authType: 'LOCAL' });
  const [saving, setSaving] = useState(false);

  const [link, setLink] = useState<{ email: string; url: string } | null>(null);
  const [copied, setCopied] = useState(false);

  const roleLabel = (r: string) => (
    r === 'SYSTEM_ADMIN' ? t('admin.roleAdmin')
      : r === 'BUILD' ? t('admin.roleBuild')
        : t('admin.roleValidator'));
  const authLabel = (a: string) =>
    a === 'ENTRA_ID' ? t('admin.authMicrosoft') : a === 'BOTH' ? t('admin.authBoth') : t('admin.authLocal');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const filters: AdminUserFilters = { limit: PAGE_SIZE, offset };
      if (search.trim()) filters.search = search.trim();
      if (roleFilter) filters.role = roleFilter;
      if (statusFilter) filters.isActive = statusFilter;
      const res = await fetchAdminUsers(filters);
      setUsers(res.data);
      setTotal(res.meta.total);
    } catch (err: any) {
      toast.error(err?.message || t('admin.loadError'));
    } finally {
      setLoading(false);
    }
  }, [search, roleFilter, statusFilter, offset, t]);

  // Debounce search + reload on filter change.
  useEffect(() => {
    const h = setTimeout(load, 250);
    return () => clearTimeout(h);
  }, [load]);

  function openCreate() {
    setForm({ email: '', displayName: '', role: 'UNIFIBER_OPERATOR', authType: 'LOCAL' });
    setDialog({ mode: 'create' });
  }
  function openEdit(u: AdminUserItem) {
    setForm({ email: u.email, displayName: u.displayName, role: u.role, authType: u.authType });
    setDialog({ mode: 'edit', user: u });
  }

  async function handleSave() {
    if (!dialog) return;
    if (!form.displayName.trim() || (dialog.mode === 'create' && !form.email.trim())) {
      toast.error(t('admin.requiredFields'));
      return;
    }
    setSaving(true);
    try {
      if (dialog.mode === 'create') {
        const res = await createUser({
          email: form.email.trim(),
          displayName: form.displayName.trim(),
          role: form.role,
          authType: form.authType,
        });
        toast.success(t('admin.createdOk'));
        if (res.activationUrl) setLink({ email: res.user.email, url: res.activationUrl });
      } else {
        await updateUser(dialog.user!.id, { displayName: form.displayName.trim(), role: form.role });
        toast.success(t('admin.updatedOk'));
      }
      setDialog(null);
      load();
    } catch (err: any) {
      toast.error(err?.message || t('admin.saveError'));
    } finally {
      setSaving(false);
    }
  }

  async function toggleActive(u: AdminUserItem) {
    try {
      await updateUser(u.id, { isActive: !u.isActive });
      toast.success(u.isActive ? t('admin.deactivatedOk') : t('admin.activatedOk'));
      load();
    } catch (err: any) {
      toast.error(err?.message || t('admin.saveError'));
    }
  }

  async function handleResend(u: AdminUserItem) {
    try {
      const res = await resendInvite(u.id);
      setLink({ email: u.email, url: res.activationUrl });
    } catch (err: any) {
      toast.error(err?.message || t('admin.saveError'));
    }
  }

  function copyLink() {
    if (!link) return;
    navigator.clipboard.writeText(link.url).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  }

  const page = Math.floor(offset / PAGE_SIZE) + 1;
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-brand-gradient flex items-center justify-center">
            <Users className="w-5 h-5 text-white" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-slate-800">{t('admin.title')}</h2>
            <p className="text-sm text-slate-500">{t('admin.subtitle', { count: total })}</p>
          </div>
        </div>
        <Button onClick={openCreate} className="bg-brand-gradient text-white hover:opacity-90">
          <UserPlus className="w-4 h-4 mr-1.5" /> {t('admin.new')}
        </Button>
      </div>

      {/* Activation link reveal */}
      {link && (
        <div className="rounded-xl border border-brand-rose/30 bg-brand-rose/[0.05] p-4 space-y-2">
          <div className="flex items-center gap-2 text-sm font-semibold text-slate-800">
            <Link2 className="w-4 h-4 text-brand-rose" /> {t('admin.linkTitle')}
          </div>
          <p className="text-xs text-slate-500">{t('admin.linkDesc', { email: link.email })}</p>
          <div className="flex items-center gap-2">
            <Input readOnly value={link.url} className="h-9 text-xs font-mono" onFocus={(e) => e.currentTarget.select()} />
            <Button variant="outline" size="sm" className="h-9 shrink-0" onClick={copyLink}>
              {copied ? <Check className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4" />}
            </Button>
            <Button variant="ghost" size="sm" className="h-9 shrink-0" onClick={() => setLink(null)}>
              {t('admin.dismiss')}
            </Button>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            value={search}
            onChange={(e) => { setOffset(0); setSearch(e.target.value); }}
            placeholder={t('admin.searchPlaceholder')}
            className="pl-9 h-9"
          />
        </div>
        <select
          value={roleFilter}
          onChange={(e) => { setOffset(0); setRoleFilter(e.target.value); }}
          className="h-9 rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-700"
        >
          <option value="">{t('admin.filterAllRoles')}</option>
          {ROLES.map((r) => <option key={r} value={r}>{roleLabel(r)}</option>)}
        </select>
        <select
          value={statusFilter}
          onChange={(e) => { setOffset(0); setStatusFilter(e.target.value as '' | 'true' | 'false'); }}
          className="h-9 rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-700"
        >
          <option value="">{t('admin.filterAllStatuses')}</option>
          <option value="true">{t('admin.statusActive')}</option>
          <option value="false">{t('admin.statusInactive')}</option>
        </select>
      </div>

      {/* Table */}
      {loading ? (
        <div className="rounded-2xl border border-slate-200 bg-white py-16 text-center text-slate-400">…</div>
      ) : users.length === 0 ? (
        <div className="rounded-2xl border border-slate-200 bg-white py-16 text-center text-slate-400">{t('admin.empty')}</div>
      ) : (
        <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 text-slate-500 text-left text-xs uppercase tracking-wide">
                <th className="px-4 py-3 font-semibold">{t('admin.colName')}</th>
                <th className="px-4 py-3 font-semibold">{t('admin.colRole')}</th>
                <th className="px-4 py-3 font-semibold">{t('admin.colAuth')}</th>
                <th className="px-4 py-3 font-semibold">{t('admin.colStatus')}</th>
                <th className="px-4 py-3 font-semibold">{t('admin.colLastLogin')}</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} className="border-t border-slate-100 hover:bg-brand-rose/[0.03]">
                  <td className="px-4 py-3">
                    <div className="font-medium text-slate-800">{u.displayName}</div>
                    <div className="text-xs text-slate-400">{u.email}</div>
                  </td>
                  <td className="px-4 py-3">
                    <span className={cn('text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wide',
                      u.role === 'SYSTEM_ADMIN' ? 'bg-violet-50 text-violet-700' : 'bg-sky-50 text-sky-700')}>
                      {roleLabel(u.role)}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-slate-600">{authLabel(u.authType)}</td>
                  <td className="px-4 py-3">
                    <span className={cn('inline-flex items-center gap-1.5 text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wide',
                      u.isActive ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-500')}>
                      <span className={cn('h-1.5 w-1.5 rounded-full', u.isActive ? 'bg-emerald-500' : 'bg-slate-400')} />
                      {u.isActive ? t('admin.statusActive') : t('admin.statusInactive')}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-slate-500 whitespace-nowrap">{formatDate(u.lastLoginAt, t('admin.never'))}</td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <div className="inline-flex items-center gap-1">
                      <button type="button" onClick={() => openEdit(u)} title={t('admin.edit')}
                        className="p-1.5 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100">
                        <Pencil className="w-4 h-4" />
                      </button>
                      {u.authType !== 'ENTRA_ID' && (
                        <button type="button" onClick={() => handleResend(u)} title={t('admin.resendLink')}
                          className="p-1.5 rounded-lg text-slate-400 hover:text-brand-rose hover:bg-slate-100">
                          <Link2 className="w-4 h-4" />
                        </button>
                      )}
                      {u.id !== me?.id && (
                        <button type="button" onClick={() => toggleActive(u)}
                          title={u.isActive ? t('admin.deactivate') : t('admin.activate')}
                          className={cn('p-1.5 rounded-lg hover:bg-slate-100', u.isActive ? 'text-slate-400 hover:text-rose-600' : 'text-slate-400 hover:text-emerald-600')}>
                          <Power className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {pageCount > 1 && (
        <div className="flex items-center justify-end gap-2 text-sm text-slate-500">
          <span>{t('admin.pageOf', { page, pages: pageCount })}</span>
          <Button variant="outline" size="sm" className="h-8" disabled={offset === 0}
            onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}>
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <Button variant="outline" size="sm" className="h-8" disabled={page >= pageCount}
            onClick={() => setOffset(offset + PAGE_SIZE)}>
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      )}

      {/* Create / edit dialog */}
      <Sheet open={dialog != null} onOpenChange={(o) => !o && setDialog(null)}>
        <SheetContent side="right" className="w-full max-w-md p-6 overflow-y-auto">
          <SheetTitle className="text-lg font-bold gradient-text">
            {dialog?.mode === 'edit' ? t('admin.editTitle') : t('admin.createTitle')}
          </SheetTitle>
          <div className="space-y-4 mt-6">
            <div>
              <label className="text-xs font-medium text-slate-600">{t('admin.fieldEmail')}</label>
              <Input
                type="email"
                value={form.email}
                disabled={dialog?.mode === 'edit'}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="name@unifiber.be"
                className="mt-1 h-9 disabled:opacity-60"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600">{t('admin.fieldName')}</label>
              <Input value={form.displayName} onChange={(e) => setForm({ ...form, displayName: e.target.value })} className="mt-1 h-9" />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600">{t('admin.fieldRole')}</label>
              <select
                value={form.role}
                onChange={(e) => setForm({ ...form, role: e.target.value })}
                className="mt-1 w-full h-9 rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-700"
              >
                {ROLES.map((r) => <option key={r} value={r}>{roleLabel(r)}</option>)}
              </select>
            </div>
            {dialog?.mode === 'create' && (
              <div>
                <label className="text-xs font-medium text-slate-600">{t('admin.fieldAuth')}</label>
                <select
                  value={form.authType}
                  onChange={(e) => setForm({ ...form, authType: e.target.value })}
                  className="mt-1 w-full h-9 rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-700"
                >
                  {AUTH_TYPES.map((a) => <option key={a} value={a}>{authLabel(a)}</option>)}
                </select>
                <p className="text-[11px] text-slate-400 mt-1">{t('admin.authHint')}</p>
              </div>
            )}
            <div className="flex items-center gap-2 pt-2">
              <Button onClick={handleSave} disabled={saving} className="bg-brand-gradient text-white hover:opacity-90">
                {saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : null}
                {dialog?.mode === 'edit' ? t('admin.save') : t('admin.create')}
              </Button>
              <Button variant="ghost" onClick={() => setDialog(null)} disabled={saving}>{t('admin.cancel')}</Button>
            </div>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
